import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_NAME, BrandMark } from "../../config/brand";
import { useAuth } from "../../providers/AuthProvider";
import { useLogout } from "../../hooks/useLogout";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";
import { Button } from "../ui/Button";

interface IconProps {
  className?: string;
}

function FilesIcon({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h4.086a1.5 1.5 0 0 1 1.06.44l1.122 1.12a1.5 1.5 0 0 0 1.06.44H19a1.5 1.5 0 0 1 1.5 1.5v8.5A1.5 1.5 0 0 1 19 18.5H5A1.5 1.5 0 0 1 3.5 17V6.5Z" />
    </svg>
  );
}

function AnalyticsIcon({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v16.5A1.5 1.5 0 0 0 4.5 21H21" />
      <path d="M7 15l4-4 3 3 5-6" />
    </svg>
  );
}

function LogoutIcon({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function ChevronIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 17l-5-5 5-5" />
    </svg>
  );
}

interface NavItemProps {
  label: string;
  icon: ReactNode;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}

function NavItem({ label, icon, active, collapsed, onClick }: NavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      title={collapsed ? label : undefined}
      className={[
        "flex items-center rounded-md text-body font-medium transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        collapsed ? "justify-center px-2 py-2" : "gap-2.5 px-3 py-2",
        active
          ? "bg-brand-50 text-brand-700"
          : "text-text-muted hover:bg-surface-muted hover:text-text",
      ].join(" ")}
    >
      <span className="shrink-0">{icon}</span>
      {collapsed ? <span className="sr-only">{label}</span> : <span className="truncate">{label}</span>}
    </button>
  );
}

/** Two-letter initials from a display name (or username fallback). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Persistent left navigation (desktop only — hidden below md, where the header
 * carries a compact user chip + logout instead). Sticky + full viewport height
 * so the user/logout footer stays visible no matter how long the page scrolls.
 * Collapsible to an icon rail (state persists across pages, §4).
 */
export function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, config } = useAuth();
  const logout = useLogout();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();

  const appName = config?.appName ?? APP_NAME;
  const displayName = user?.displayName ?? user?.username ?? "Account";
  const isAdmin = user?.role === "admin";

  const filesActive = pathname === "/" || pathname.startsWith("/folder");
  const analyticsActive = pathname.startsWith("/analytics");

  return (
    <aside
      className={[
        "sticky top-0 hidden h-screen shrink-0 flex-col self-start border-r border-surface-border bg-surface transition-[width] duration-200 md:flex",
        collapsed ? "w-16" : "w-60",
      ].join(" ")}
    >
      {/* Brand + collapse toggle */}
      <div
        className={[
          "flex shrink-0 border-b border-surface-border px-3 py-4",
          collapsed ? "flex-col items-center gap-2" : "items-center justify-between",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex min-w-0 items-center gap-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label={`${appName} — Home`}
          title={collapsed ? appName : undefined}
        >
          <BrandMark className="h-6 w-6 shrink-0 text-brand-600" />
          {!collapsed ? (
            <span className="min-w-0 truncate text-brand font-semibold text-text">{appName}</span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-muted hover:bg-surface-muted hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <ChevronIcon className={collapsed ? "h-4 w-4 rotate-180" : "h-4 w-4"} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3" aria-label="Primary">
        {isAdmin ? (
          <NavItem
            label="Analytics"
            icon={<AnalyticsIcon />}
            active={analyticsActive}
            collapsed={collapsed}
            onClick={() => navigate("/analytics")}
          />
        ) : null}
        <NavItem
          label="Files"
          icon={<FilesIcon />}
          active={filesActive}
          collapsed={collapsed}
          onClick={() => navigate("/")}
        />
      </nav>

      {/* User + logout footer (always visible thanks to sticky h-screen) */}
      <div
        className={[
          "flex shrink-0 flex-col gap-3 border-t border-surface-border p-3",
          collapsed ? "items-center" : "",
        ].join(" ")}
      >
        <div className={collapsed ? "" : "flex min-w-0 items-center gap-2.5"}>
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white"
            aria-hidden="true"
            title={collapsed ? `${displayName} · ${isAdmin ? "Admin" : "Investor"}` : undefined}
          >
            {initials(displayName)}
          </span>
          {!collapsed ? (
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-body font-medium text-text" title={displayName}>
                {displayName}
              </span>
              <span className="text-xs text-text-muted">{isAdmin ? "Admin" : "Investor"}</span>
            </span>
          ) : null}
        </div>

        {collapsed ? (
          <button
            type="button"
            onClick={() => void logout()}
            aria-label="Logout"
            title="Logout"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-surface-border text-text hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <LogoutIcon />
          </button>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => void logout()} className="w-full">
            Logout
          </Button>
        )}
      </div>
    </aside>
  );
}
